import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Text } from '@cotera/client/app/components/ui';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Button } from '@cotera/client/v0/components/ui/button';
import { useToolCallUpstreamSamples } from '../../../hooks/use-tool-call-upstream-samples';
import { useEmitEventNameSuggestions } from '../../../hooks/use-emit-event-name-suggestions';
import { asRecord, readString } from '../../utils';
import { buildUpstreamSources } from './variable-picker-data';
import {
  UpstreamRefChip,
  UpstreamRefPicker,
  UpstreamRefPickerButton,
} from './upstream-ref-picker';
import type { WorkflowNodeEditorProps } from './types';

type PayloadBindingRow = {
  readonly id: string;
  readonly key: string;
  readonly binding: string;
};

function readPayloadBindingRows(data: Record<string, unknown> | null): {
  rows: PayloadBindingRow[];
  nextId: number;
} {
  const raw = data?.['payloadBindings'];
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return { rows: [], nextId: 1 };
  }
  const entries = Object.entries(raw as Record<string, unknown>);
  const rows: PayloadBindingRow[] = [];
  let nextId = 1;
  for (const [key, value] of entries) {
    if (typeof value !== 'string') {
      continue;
    }
    rows.push({ id: `row-${nextId}`, key, binding: value });
    nextId += 1;
  }
  return { rows, nextId };
}

/**
 * Config editor for an emit-event node: event name plus optional named payload
 * fields bound to upstream output refs. Reports `null` while the event name is
 * blank.
 */
export const EmitEventNodeEditor: React.FC<WorkflowNodeEditorProps> = ({
  node,
  onChange,
}) => {
  const data = asRecord(node.data);
  const seeded = readPayloadBindingRows(data);
  const [eventName, setEventName] = useState<string>(
    readString(data, 'eventName') ?? ''
  );
  const [rows, setRows] = useState<PayloadBindingRow[]>(seeded.rows);
  const [nextRowId, setNextRowId] = useState(seeded.nextId);

  const [seededNodeId, setSeededNodeId] = useState(node.id);
  if (seededNodeId !== node.id) {
    setSeededNodeId(node.id);
    const next = asRecord(node.data);
    const nextBindings = readPayloadBindingRows(next);
    setEventName(readString(next, 'eventName') ?? '');
    setRows(nextBindings.rows);
    setNextRowId(nextBindings.nextId);
  }

  const { upstreamSamples } = useToolCallUpstreamSamples(node.id);
  const sources = useMemo(
    () => buildUpstreamSources(upstreamSamples),
    [upstreamSamples]
  );
  const sourceLabels = useMemo(
    () =>
      new Map(
        upstreamSamples.map(
          (sample) => [sample.sourceName, sample.label] as const
        )
      ),
    [upstreamSamples]
  );

  const eventNameSuggestions = useEmitEventNameSuggestions();

  useEffect(() => {
    const trimmedName = eventName.trim();
    if (trimmedName === '') {
      onChange(null);
      return;
    }
    const payloadBindings: Record<string, string> = {};
    for (const row of rows) {
      const key = row.key.trim();
      const binding = row.binding.trim();
      if (key === '' || binding === '') {
        continue;
      }
      payloadBindings[key] = binding;
    }
    onChange({
      type: 'emit-event',
      data: {
        eventName: trimmedName,
        ...(Object.keys(payloadBindings).length > 0 ? { payloadBindings } : {}),
      },
    });
  }, [eventName, rows, onChange]);

  const listId = `emit-event-names-${node.id}`;

  const updateRow = (
    id: string,
    patch: Partial<Pick<PayloadBindingRow, 'key' | 'binding'>>
  ): void => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  };

  const removeRow = (id: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const addRow = (): void => {
    const id = `row-${nextRowId}`;
    setNextRowId((n) => n + 1);
    setRows((prev) => [...prev, { id, key: '', binding: '' }]);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Text.Caption className="text-muted-foreground block">
          Event name
        </Text.Caption>
        <Text.Caption className="text-muted-foreground/80 block">
          Workflows with an event trigger listening for this name will run when
          this step emits.
        </Text.Caption>
        <Input
          value={eventName}
          list={eventNameSuggestions.length > 0 ? listId : undefined}
          placeholder="order.created"
          onChange={(event) => setEventName(event.target.value)}
        />
        {eventNameSuggestions.length > 0 ? (
          <datalist id={listId}>
            {eventNameSuggestions.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
        ) : null}
      </div>

      <div className="flex flex-col gap-2">
        <Text.Caption className="text-muted-foreground block">
          Payload fields
        </Text.Caption>
        <Text.Caption className="text-muted-foreground/80 block">
          Optional named fields built from previous step outputs. Each key
          becomes a top-level property on the event payload.
        </Text.Caption>
        <div className="flex flex-col gap-3">
          {rows.map((row) => (
            <div
              key={row.id}
              className="border-input flex flex-col gap-2 rounded-md border p-3"
            >
              <div className="flex items-center gap-2">
                <Input
                  value={row.key}
                  placeholder="Field name"
                  className="h-8 flex-1"
                  onChange={(event) =>
                    updateRow(row.id, { key: event.target.value })
                  }
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  title="Remove field"
                  onClick={() => removeRow(row.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                {row.binding.trim() !== '' ? (
                  <UpstreamRefChip
                    refValue={row.binding}
                    sourceLabels={sourceLabels}
                    onRemove={() => updateRow(row.id, { binding: '' })}
                  />
                ) : (
                  <UpstreamRefPicker
                    sources={sources}
                    onPick={(ref) => updateRow(row.id, { binding: ref })}
                  >
                    <button
                      type="button"
                      className="border-input bg-background hover:bg-muted/60 text-muted-foreground flex h-8 flex-1 items-center justify-start rounded-md border px-3 text-left text-sm"
                    >
                      {sources.length > 0
                        ? 'Select a field…'
                        : 'No upstream fields yet'}
                    </button>
                  </UpstreamRefPicker>
                )}
                {row.binding.trim() !== '' ? (
                  <UpstreamRefPickerButton
                    sources={sources}
                    onPick={(ref) => updateRow(row.id, { binding: ref })}
                    title="Change field"
                  />
                ) : null}
              </div>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          className="h-9 justify-start gap-2"
          onClick={addRow}
        >
          <Plus className="h-4 w-4" />
          Add payload field
        </Button>
      </div>
    </div>
  );
};
